import { ENV } from "env";
import { logInfo } from "api/accounts-customer";

export const DEFAULT_CONFIG = {
  logToRudderstack: false, // keeping this here incase we bring back Rudderstack
  logToConsole: ENV === "sandbox" || (ENV || "").includes("staging"),
  sendToLogs: true,
  sendToDebugFnIfAvailable: true,
};

/**
 * @name Analytics
 * @function
 * @param {Object} config - Configuration for AnalyticsInstance
 * @param {boolean} config.logToRudderstack
 * @param {boolean} config.logToConsole
 * @param {boolean} config.sendToLogs
 * @param {boolean} config.sendToDebugFnIfAvailable
 * @returns {AnalyticsInstance}
 * @description Returns an object that can be used to send an
 * analytic message and payload to any number of destinations.
 * Primarily expect it to be used to send to a service like
 * Rudderstack or Segment (data pipelines), although here you
 * can log to console or our logging system (Datadog) or a debug
 * function (if passed in).
 * This can be used independant of anything (aside from making sure
 * the rudderstack lib is loaded on to the window object), although I
 * expect it to mostly be used within the useAnalytic context provider
 * since there's a number of additional information we can tie
 * into the message payload if called from there.
 */
const Analytics = (config = DEFAULT_CONFIG) => {
  const { logToConsole, sendToLogs, sendToDebugFnIfAvailable, logToRudderstack } = config;

  const identify = ({ title, account_id, payload, debug, jwt = {} }) => {
    if (logToRudderstack && window.rudderanalytics) {
      window.rudderanalytics.identify(account_id, payload);
    }
    if (logToConsole) console.log(`${title}`, payload); // eslint-disable-line no-console
    if (sendToDebugFnIfAvailable && debug) debug(`${title}`, payload);
    if (sendToLogs) logInfo(jwt, {
      analyticsTitle: title,
      analyticType: "identify",
      payload
    });
  };

  const page = ({ title, categoryStr, nameStr, payload, jwt = {}, debug }) => {
    if (logToRudderstack && window.rudderanalytics) {
      window.rudderanalytics.page(categoryStr, nameStr, payload);
    }
    const newTitle = `${title}, categoryStr="${categoryStr}", nameStr="${nameStr}"`;
    if (logToConsole) {
      console.log(newTitle, payload); // eslint-disable-line no-console
    }
    if (sendToDebugFnIfAvailable && debug) debug(`${newTitle}`, payload);
    if (sendToLogs) logInfo(jwt, {
      analyticsTitle: newTitle,
      analyticType: "page",
      categoryStr,
      nameStr,
      payload
    });
  };

  const track = ({ title, eventStr, payload, debug, jwt = {} }) => {
    if (logToRudderstack && window.rudderanalytics) {
      window.rudderanalytics.track(eventStr, payload);
    }
    const newTitle = `${title}, eventStr="${eventStr}"`;
    if (logToConsole) console.log(newTitle, payload); // eslint-disable-line no-console
    if (sendToDebugFnIfAvailable && debug) debug(newTitle, payload);
    if (sendToLogs) logInfo(jwt, {
      analyticsTitle: newTitle,
      analyticType: "track",
      eventStr,
      payload
    });
  };

  return {
    identify,
    page,
    track,
  };
};

export default Analytics;
